import { z } from "zod";

// Event-sourcing taxonomy (shared by server append/projectors and web read typing).
export * from "./events";

// Canonical upload size limit shared by clients and server-side validation.
export const MAX_UPLOAD_FILE_BYTES = 10 * 1024 * 1024; // 10MB

/**
 * Decoded byte length of a base64 (or data-URL) string, without allocating the
 * decoded buffer. Used to size-guard upload payloads before they hit storage.
 */
export function base64DecodedByteLength(value: string): number {
  const base64 = value.includes(",")
    ? value.slice(value.indexOf(",") + 1)
    : value;
  const length = base64.length;
  if (length === 0) {
    return 0;
  }
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((length * 3) / 4) - padding;
}

/** Zod refinement asserting a base64 payload stays within MAX_UPLOAD_FILE_BYTES. */
export const withinUploadSizeLimit = (value: string): boolean =>
  base64DecodedByteLength(value) <= MAX_UPLOAD_FILE_BYTES;

export const UPLOAD_TOO_LARGE_MESSAGE = "File exceeds the 10MB limit";

// Profile update schema - used by both frontend and backend
export const profileUpdateSchema = z.object({
  fullName: z.string().min(1, "Full name is required").optional(),
  company: z.string().min(1, "Company is required").optional(),
  jobTitle: z.string().min(1, "Job title is required").optional(),
  phoneNumber: z.string().optional(),
});

// Form data schema for the profile edit form (frontend only)
export const profileFormSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  company: z.string().min(1, "Company is required"),
  jobTitle: z.string().min(1, "Job title is required"),
  phoneNumber: z.string().optional(),
});

// Self-service system role schema
export const selfServiceSystemRoleSchema = z.object({
  systemRole: z.enum(["worker", "site_manager"] as const),
});

// Admin-managed system role schema
export const adminSystemRoleSchema = z.object({
  systemRole: z.enum(["worker", "site_manager", "admin"] as const),
});

// Type inference from schemas
export type ProfileUpdateData = z.infer<typeof profileUpdateSchema>;
export type ProfileFormData = z.infer<typeof profileFormSchema>;
export type SelfServiceSystemRoleData = z.infer<
  typeof selfServiceSystemRoleSchema
>;
export type AdminSystemRoleData = z.infer<typeof adminSystemRoleSchema>;

// Emergency contact schema
// UK phone validation (simple):
// - Accepts E.164 format: +44 followed by 10 digits
// - Accepts national format: 0 followed by 10 digits
// - Ignores spaces, dashes, and parentheses when validating
const isValidUkPhone = (value: string) => {
  const normalized = value.replace(/[^\d+]/g, "");
  const e164 = /^\+44\d{10}$/;
  const national = /^0\d{10}$/;
  return e164.test(normalized) || national.test(normalized);
};

export const emergencyContactSchema = z.object({
  name: z.string().min(1, "Name is required"),
  relationship: z.string().optional(),
  phoneNumber: z
    .string()
    .min(1, "Phone number is required")
    .refine(isValidUkPhone, "Enter a valid UK phone number"),
  isPrimary: z.boolean().optional(),
});

export type EmergencyContactData = z.infer<typeof emergencyContactSchema>;

// Worker certificate upload schema
const allowedCertificateMimeTypes = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
] as const;

export const workerCertificateUploadSchema = z.object({
  fileBase64: z
    .string()
    .min(1, "File content is required")
    .refine(withinUploadSizeLimit, UPLOAD_TOO_LARGE_MESSAGE),
  fileMimeType: z
    .string()
    .min(1, "File type is required")
    .transform((value) => value.trim().toLowerCase())
    .refine(
      (value) =>
        allowedCertificateMimeTypes.includes(
          value as (typeof allowedCertificateMimeTypes)[number]
        ),
      "Only PDF and JPEG files are supported"
    ),
});

export type WorkerCertificateUploadData = z.infer<
  typeof workerCertificateUploadSchema
>;

export default {
  profileUpdateSchema,
  profileFormSchema,
  selfServiceSystemRoleSchema,
  adminSystemRoleSchema,
  emergencyContactSchema,
  workerCertificateUploadSchema,
};
